import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { parseExpression } from 'cron-parser'
import cronstrue from 'cronstrue/i18n'
import { unref } from 'vue'

dayjs.extend(utc)
dayjs.extend(timezone)

export const SCHEDULE_COMMAND_REFERENCE_TIMEZONE = 'America/Sao_Paulo'

const QUARTZ_FIELD_COUNT = 6
const SIMPLE_NUMERIC_FIELD = /^\d+$/

const parseCronFields = (cronExpression: string) => cronExpression.trim().split(/\s+/)
const joinCronFields = (fields: string[]) => fields.join(' ')

const isSimpleNumericToken = (value: string) => SIMPLE_NUMERIC_FIELD.test(value)

const isSupportedAbsoluteQuartzCron = (fields: string[]) => {
  if (fields.length !== QUARTZ_FIELD_COUNT) return false

  const [second, minute, hour, dayOfMonth, month, dayOfWeek] = fields

  if (![second, minute, hour].every(isSimpleNumericToken)) return false

  const simpleDayOfMonth = dayOfMonth === '*' || dayOfMonth === '?' || isSimpleNumericToken(dayOfMonth)
  const simpleMonth = month === '*' || isSimpleNumericToken(month)
  const simpleDayOfWeek = dayOfWeek === '*' || dayOfWeek === '?' || isSimpleNumericToken(dayOfWeek)

  return simpleDayOfMonth && simpleMonth && simpleDayOfWeek
}

const getQuartzDayOfWeek = (date: dayjs.Dayjs) => {
  const weekDay = date.day()
  return weekDay === 0 ? 1 : weekDay + 1
}

const convertQuartzDayOfWeekValueToStandard = (value: number) => {
  if (value === 1) return 0
  if (value >= 2 && value <= 7) return value - 1
  return value
}

const convertQuartzDayOfWeekFieldToStandard = (value: string) =>
  value.replace(/\d+/g, (match) => String(convertQuartzDayOfWeekValueToStandard(Number(match))))

const normalizeQuartzCronForFrontendParser = (cronExpression: string) => {
  const fields = parseCronFields(cronExpression)

  if (fields.length !== QUARTZ_FIELD_COUNT) {
    return cronExpression
  }

  const nextFields = [...fields]
  nextFields[5] = convertQuartzDayOfWeekFieldToStandard(nextFields[5])

  return joinCronFields(nextFields)
}

const formatConvertedQuartzCron = (fields: string[], convertedDate: dayjs.Dayjs) => {
  const [, , , dayOfMonth, month, dayOfWeek] = fields

  const nextFields = [
    String(convertedDate.second()),
    String(convertedDate.minute()),
    String(convertedDate.hour()),
    dayOfMonth,
    month,
    dayOfWeek,
  ]

  if (dayOfWeek === '?' && dayOfMonth !== '*') {
    nextFields[3] = String(convertedDate.date())
    if (month !== '*') {
      nextFields[4] = String(convertedDate.month() + 1)
    }
  } else if (dayOfMonth === '?' && dayOfWeek !== '*') {
    nextFields[5] = String(getQuartzDayOfWeek(convertedDate))
  }

  return nextFields.join(' ')
}

export const getBrowserTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || SCHEDULE_COMMAND_REFERENCE_TIMEZONE

export const getBrowserUtcOffsetMinutes = () => dayjs().tz(getBrowserTimeZone()).utcOffset()

export function convertScheduleCommandCronTimeZone(
  cronExpression: string,
  fromTimeZone: string,
  toTimeZone: string,
) {
  const fields = parseCronFields(cronExpression)

  if (!isSupportedAbsoluteQuartzCron(fields) || fromTimeZone === toTimeZone) {
    return cronExpression
  }

  try {
    const normalizedCron = normalizeQuartzCronForFrontendParser(cronExpression)
    const interval = parseExpression(normalizedCron, { tz: fromTimeZone })
    const nextOccurrence = dayjs(interval.next().toISOString())
    const convertedOccurrence = nextOccurrence.tz(toTimeZone)

    return formatConvertedQuartzCron(fields, convertedOccurrence)
  } catch {
    return cronExpression
  }
}

export const convertScheduleCommandCronToReferenceTimeZone = (
  cronExpression: string,
  userTimeZone = getBrowserTimeZone(),
) =>
  convertScheduleCommandCronTimeZone(
    cronExpression,
    userTimeZone,
    SCHEDULE_COMMAND_REFERENCE_TIMEZONE,
  )

export const convertScheduleCommandCronFromReferenceTimeZone = (
  cronExpression: string,
  userTimeZone = getBrowserTimeZone(),
) =>
  convertScheduleCommandCronTimeZone(
    cronExpression,
    SCHEDULE_COMMAND_REFERENCE_TIMEZONE,
    userTimeZone,
  )

export function getNextDateTroughCron(
  cronExpression: string,
  timeZone: string = getBrowserTimeZone(),
) {
  try {
    const normalizedCron = normalizeQuartzCronForFrontendParser(cronExpression)
    const interval = parseExpression(normalizedCron, { tz: timeZone })
    return interval.next().toISOString()
  } catch (error) {
    throw new Error(' Cron Inválido')
  }
}

export function formatCron(cron: string | undefined, lang: any) {
  if (!cron) return ''
  try {
    const rawLocale = String(unref(lang) || 'pt').toLowerCase()
    const locale = rawLocale.startsWith('pt')
      ? 'pt_BR'
      : rawLocale.startsWith('es')
        ? 'es'
        : 'en'

    return cronstrue.toString(cron, { locale, dayOfWeekStartIndexZero: false })
  } catch {
    return cron
  }
}
